import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import type { IMcBimItem } from "@multiconsult-group/multiplacer-core";
import { FiMaximize2 } from "react-icons/fi";
import { RoomSectionViewsPanelContent } from "../RoomSectionViews/RoomSectionViewsPanelContent";
import type { IViewerSelectedSection } from "../ViewerRedux/viewerSlice";
import { useGetMcbimItemsInSpaceQuery, useGetMcbimSingleMcbimSpaceQuery } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectCompCodePath } from "../globalSelectors";
import { DrofusOccurrenceRoomViewerCanvas } from "./DrofusOccurrenceRoomViewerCanvas";
import {
  selectDrofusOccurrenceRoomViewerItemsApiArg,
  selectDrofusOccurrenceRoomViewerModelFromRedux,
  selectDrofusOccurrenceRoomViewerSpaceApiArg,
} from "./drofusOccurrenceRoomViewerSelectors";
import {
  selectDrofusOccurrenceRoomViewerState,
  setDrofusOccurrenceRoomViewerContext,
  setDrofusOccurrenceRoomViewerShowLengthComponents,
} from "./drofusForekomsterSlice";

type DrofusOccurrenceRoomViewerProps = {
  projectNo: number;
  roomFunctionNo?: string | null;
  articleCompCode?: string | null;
  articleToBeDrawn?: boolean | null;
};

export const DrofusOccurrenceRoomViewer: React.FC<DrofusOccurrenceRoomViewerProps> = ({
  projectNo,
  roomFunctionNo,
  articleCompCode,
  articleToBeDrawn,
}) => {
  const dispatch = useAppDispatch();
  const [showFullViewer, setShowFullViewer] = useState(false);
  const [selectedSection, setSelectedSection] = useState<IViewerSelectedSection>("isometric");
  const compCodePath = useAppSelector(selectCompCodePath);
  const roomViewerState = useAppSelector(selectDrofusOccurrenceRoomViewerState);
  const spaceArgs = useAppSelector(selectDrofusOccurrenceRoomViewerSpaceApiArg);
  const itemsArgs = useAppSelector(selectDrofusOccurrenceRoomViewerItemsApiArg);
  const viewerModel = useAppSelector(selectDrofusOccurrenceRoomViewerModelFromRedux);

  useEffect(() => {
    dispatch(
      setDrofusOccurrenceRoomViewerContext({
        projectNo: Number.isFinite(projectNo) && projectNo > 0 ? projectNo : null,
        roomFunctionNo,
        articleCompCode,
        articleToBeDrawn,
      })
    );
  }, [articleCompCode, articleToBeDrawn, dispatch, projectNo, roomFunctionNo]);

  const { data: space, isFetching: isFetchingSpace } = useGetMcbimSingleMcbimSpaceQuery(
    spaceArgs ?? skipToken
  );
  const { isFetching: isFetchingItems } = useGetMcbimItemsInSpaceQuery(itemsArgs ?? skipToken);

  const isFetching = isFetchingSpace || isFetchingItems;

  if (!roomFunctionNo) {
    return (
      <Paper bg="gray.0" radius="sm" p="md">
        <Text size="sm" c="dimmed">
          Romnummer mangler.
        </Text>
      </Paper>
    );
  }

  if (!compCodePath) {
    return (
      <Paper bg="gray.0" radius="sm" p="md">
        <Text size="sm" c="dimmed">
          Prosjektet mangler compCodePath.
        </Text>
      </Paper>
    );
  }

  const viewerHeader = (
    <Group justify="space-between" align="center">
      <Group gap="sm">
        <Text size="sm" fw={700}>
          Romviewer
        </Text>
        <Switch
          size="xs"
          checked={roomViewerState.showLengthComponents}
          onChange={(event) =>
            dispatch(setDrofusOccurrenceRoomViewerShowLengthComponents(event.currentTarget.checked))
          }
          label="Vis lengdekomponenter"
        />
      </Group>
      <Group gap="xs">
        {isFetching && (
          <Badge variant="light" color="gray" radius="sm">
            Laster IFC
          </Badge>
        )}
        <Badge
          variant="light"
          color={
            viewerModel.showMissingModeledMatchWarning
              ? "red"
              : viewerModel.matchCount > 0
                ? "orange"
                : "gray"
          }
          radius="sm"
        >
          {viewerModel.matchCount} match
        </Badge>
        <Badge variant="light" color="gray" radius="sm">
          {viewerModel.visibleItemCount} komponenter
        </Badge>
        {!roomViewerState.showLengthComponents && viewerModel.hiddenLengthItemCount > 0 && (
          <Badge variant="light" color="gray" radius="sm">
            {viewerModel.hiddenLengthItemCount} skjult
          </Badge>
        )}
        <Tooltip label="Åpne i full visning">
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Åpne romviewer i full visning"
            onClick={() => setShowFullViewer(true)}
          >
            <FiMaximize2 size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );

  return (
    <>
      <Stack gap="xs">
        {viewerHeader}
        {viewerModel.showMissingModeledMatchWarning && (
          <Alert color="yellow" variant="light" radius="sm">
            Artikkelen er satt til <strong>Skal modelleres</strong>, men ingen komponent med samme
            compCode ble funnet i rommet.
          </Alert>
        )}
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.15fr) minmax(360px, 0.85fr)",
            gap: 12,
            alignItems: "stretch",
          }}
        >
          <Box h={480} style={{ minWidth: 0 }}>
            <DrofusOccurrenceRoomViewerCanvas />
          </Box>
          <Box h={480} style={{ minWidth: 0 }}>
            <RoomSectionViewsPanelContent
              mcbimSpace={space as IMcBimItem | null | undefined}
              renderItems={viewerModel.sectionRenderItems}
              sectionCenterCoordinate={viewerModel.sectionCenterCoordinate}
              selectedSection={selectedSection}
              onSelectedSectionChange={setSelectedSection}
              background="#f8f9fa"
            />
          </Box>
        </Box>
      </Stack>

      <Modal
        opened={showFullViewer}
        onClose={() => setShowFullViewer(false)}
        title={`Romviewer ${roomFunctionNo}`}
        size="95vw"
        centered
        styles={{ content: { height: "92vh" }, body: { height: "calc(92vh - 60px)" } }}
      >
        <Stack gap="xs" h="100%">
          {viewerHeader}
          {viewerModel.showMissingModeledMatchWarning && (
            <Alert color="yellow" variant="light" radius="sm">
              Artikkelen er satt til <strong>Skal modelleres</strong>, men ingen komponent med samme
              compCode ble funnet i rommet.
            </Alert>
          )}
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.15fr) minmax(360px, 0.85fr)",
              gap: 12,
            }}
          >
            <Box h="100%" style={{ minWidth: 0 }}>
              <DrofusOccurrenceRoomViewerCanvas />
            </Box>
            <RoomSectionViewsPanelContent
              mcbimSpace={space as IMcBimItem | null | undefined}
              renderItems={viewerModel.sectionRenderItems}
              sectionCenterCoordinate={viewerModel.sectionCenterCoordinate}
              selectedSection={selectedSection}
              onSelectedSectionChange={setSelectedSection}
            />
          </Box>
        </Stack>
      </Modal>
    </>
  );
};
